/**
 * HouseholdLiveInsights — sticky right-column panel for /household-calculator.
 *
 * Local mirror of the household backend math so users see Total Cost,
 * Water Removed, Dry Weight and Energy update as they type. Mirrors the
 * BusinessCalc LiveInsightsPanel visual language (amber accents, tabular
 * numbers, empty-state hint).
 */
import React, { useMemo } from 'react';
import { Droplet, Info, Package, Zap, Home } from 'lucide-react';

const HouseholdLiveInsights = ({ items = [], selectedModel = null, outputs = null, testId = 'household-live-insights' }) => {
  const local = useMemo(() => {
    const n = (v) => parseFloat(v) || 0;
    const toGrams = (val, unit) => {
      const w = n(val);
      switch ((unit || 'g').toLowerCase()) {
        case 'lb': case 'lbs': return w * 453.592;
        case 'oz': return w * 28.3495;
        case 'kg': return w * 1000;
        default: return w;
      }
    };

    let wetG = 0;
    let cost = 0;
    let maxCycle = 0;
    let waterPctSum = 0;
    let waterPctCount = 0;

    items.forEach((item) => {
      wetG += toGrams(item.wet_weight, item.weight_unit || 'g');
      cost += n(item.food_cost) || n(item.wet_weight) * n(item.cost_per_unit);
      const cycle = n(item.cycle_time_hours);
      if (cycle > maxCycle) maxCycle = cycle;
      // Default water content: 85% for produce, 65% for meats — fallback 80%
      const mc = n(item.custom_moisture);
      if (mc > 0) {
        waterPctSum += mc;
        waterPctCount += 1;
      }
    });

    const waterPct = waterPctCount > 0 ? waterPctSum / waterPctCount : 80;
    const waterG = wetG * (waterPct / 100);
    const dryG = wetG - waterG;

    // Energy: kW × hours. Default 1.5kW average across small/medium home freeze dryers.
    const kw = n(selectedModel?.power_kw) || 1.5;
    const kwh = kw * (maxCycle || 24);
    const rate = n(items[0]?.electricity_rate) || 0.12;
    const energyCost = kwh * rate;

    return {
      wetG, waterG, dryG, kwh, energyCost,
      totalCost: cost + energyCost,
      itemCount: items.length,
    };
  }, [items, selectedModel]);

  const view = outputs
    ? {
        totalCost: parseFloat(outputs.total_batch_cost) || local.totalCost,
        wetG: parseFloat(outputs.total_wet_weight_g) || local.wetG,
        dryG: parseFloat(outputs.total_dry_weight_g) || local.dryG,
        waterG: parseFloat(outputs.total_water_removed_g) || local.waterG,
        kwh: parseFloat(outputs.total_energy_kwh) || local.kwh,
        itemCount: local.itemCount,
        source: 'server',
      }
    : { ...local, source: 'local' };

  const enough = view.itemCount > 0 && view.wetG > 0;
  const fmt = (v, d = 2) => (v == null || isNaN(v) ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: d }));
  const gramsToLb = (g) => g / 453.592;

  return (
    <aside className="pro-card p-5 sm:p-6 rounded-3xl" data-testid={testId} aria-label="Live batch insights">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-heading text-lg font-extrabold tracking-tight">Live insights</h3>
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          {view.source === 'server' ? 'Server' : 'Live preview'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        {enough
          ? 'Numbers update as you type. Hit Calculate for the full report.'
          : 'Add a food item and wet weight to unlock the preview.'}
      </p>

      {/* Headline — total batch cost */}
      <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 mb-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Projected batch cost</div>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          {enough || view.source === 'server' ? (
            <span className="font-heading text-3xl sm:text-4xl font-extrabold tabular-nums text-foreground" data-testid="hh-live-total-cost">
              ${fmt(view.totalCost, 2)}
            </span>
          ) : (
            <span className="font-heading text-3xl sm:text-4xl font-extrabold tabular-nums text-foreground/40" data-testid="hh-live-total-cost">
              $—
            </span>
          )}
          <span className="text-xs text-muted-foreground">/ batch</span>
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <Home className="w-3.5 h-3.5 text-emerald-600" />
          <span className="text-xs font-semibold text-foreground/80">
            {view.itemCount} {view.itemCount === 1 ? 'item' : 'items'} · food + energy
          </span>
        </div>
      </div>

      {/* Metric rows */}
      <div className="space-y-2.5">
        <Row icon={Package} label="Wet weight" value={enough || view.source === 'server' ? `${fmt(gramsToLb(view.wetG), 2)} lb` : '—'} testId="hh-live-wet" />
        <Row icon={Package} label="Dry weight" value={enough || view.source === 'server' ? `${fmt(gramsToLb(view.dryG), 2)} lb` : '—'} testId="hh-live-dry" />
        <Row icon={Droplet} label="Water removed" value={enough || view.source === 'server' ? `${fmt(gramsToLb(view.waterG), 2)} lb` : '—'} testId="hh-live-water" />
        <Row icon={Zap} label="Energy" value={enough || view.source === 'server' ? `${fmt(view.kwh, 1)} kWh` : '—'} testId="hh-live-energy" />
      </div>

      {!enough && view.source !== 'server' && (
        <div className="mt-5 flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
          <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <span>Add at least one item with a <strong className="text-foreground">wet weight</strong> to see the preview.</span>
        </div>
      )}
    </aside>
  );
};

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center justify-between gap-3 pb-2 border-b border-border/60 last:border-b-0 last:pb-0">
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-muted-foreground">
      <Icon className="w-3.5 h-3.5 text-primary/70" />
      {label}
    </span>
    <span className="font-heading text-sm font-extrabold tabular-nums text-foreground" data-testid={testId}>
      {value}
    </span>
  </div>
);

export default HouseholdLiveInsights;
